import type { PagedListReturn } from '@cks-internal/react-base';
import ProjectContainerStatus from 'src/enums/companyProjects/ProjectContainerStatus';
import type {
  MyAssignedProjectContainersResultModel,
  ProjectContainerCompanyGroupModel,
  ProjectContainerListItemModel
} from 'src/features/companyProjects/navigation/types/MyAssignedProjectContainersModels';
import type { ProjectContainerTableModel } from 'src/features/companyProjects/projectContainers/manageProjects/types/ProjectContainerTableModel';
import type { ProjectContainerListEvent } from './projectContainerEventTypes';

const mapStatusStringToEnum = (status: string | undefined): ProjectContainerStatus | undefined => {
  if (!status) return undefined;
  switch (status) {
    case 'Active':
      return ProjectContainerStatus.Active;
    case 'Passive':
      return ProjectContainerStatus.Passive;
    default:
      return undefined;
  }
};

const cloneAssignedGroups = (groups: ProjectContainerCompanyGroupModel[]): ProjectContainerCompanyGroupModel[] =>
  groups.map((group) => ({
    ...group,
    Containers: group.Containers.map<ProjectContainerListItemModel>((c) => ({ ...c }))
  }));

const findAssignedContainer = (
  groups: ProjectContainerCompanyGroupModel[],
  containerId: number
): { groupIndex: number; containerIndex: number } | null => {
  for (let gi = 0; gi < groups.length; gi += 1) {
    const containers = groups[gi].Containers;
    const ci = containers.findIndex((c) => c.Id === containerId);
    if (ci !== -1) {
      return { groupIndex: gi, containerIndex: ci };
    }
  }
  return null;
};

const ensureAssignedGroup = (
  groups: ProjectContainerCompanyGroupModel[],
  companyId: number
): { groups: ProjectContainerCompanyGroupModel[]; groupIndex: number } => {
  const existingIndex = groups.findIndex((g) => g.CompanyId === companyId);
  if (existingIndex !== -1) {
    return { groups, groupIndex: existingIndex };
  }

  const newGroup: ProjectContainerCompanyGroupModel = {
    CompanyId: companyId,
    CompanyName: '',
    Containers: []
  };

  return {
    groups: [...groups, newGroup],
    groupIndex: groups.length
  };
};

export const applyProjectContainerListEventToAssignedContainersState = (
  current: MyAssignedProjectContainersResultModel | undefined,
  event: ProjectContainerListEvent,
  currentUserId?: string
): MyAssignedProjectContainersResultModel | undefined => {
  if (!current) return current;

  const statusEnum = mapStatusStringToEnum(event.status);
  if (!statusEnum) return current;

  let activeGroups = cloneAssignedGroups(current.Active ?? []);
  let passiveGroups = cloneAssignedGroups(current.Passive ?? []);

  // Locate existing container, if any
  const activeLoc = findAssignedContainer(activeGroups, event.containerId);
  const passiveLoc = !activeLoc ? findAssignedContainer(passiveGroups, event.containerId) : null;

  const targetIsActive = statusEnum === ProjectContainerStatus.Active;
  const isAssignedToCurrentUser =
    !currentUserId ||
    event.assignedToUserId === currentUserId ||
    (event.additionalAssignedToUserIds ?? []).includes(currentUserId);

  const removeFromLocation = (
    groups: ProjectContainerCompanyGroupModel[],
    loc: { groupIndex: number; containerIndex: number } | null
  ): ProjectContainerCompanyGroupModel[] => {
    if (!loc) return groups;
    const { groupIndex, containerIndex } = loc;
    const group = groups[groupIndex];
    const containers = group.Containers.filter((_, idx) => idx !== containerIndex);
    const updatedGroup: ProjectContainerCompanyGroupModel = {
      ...group,
      Containers: containers
    };
    const clone = [...groups];
    clone[groupIndex] = updatedGroup;
    return clone;
  };

  // If we know the current user and this event is for a different assignment set,
  // remove the container from "my assigned" lists.
  if (!isAssignedToCurrentUser) {
    const changedActive = activeLoc != null;
    const changedPassive = passiveLoc != null;

    if (!changedActive && !changedPassive) {
      return current;
    }

    activeGroups = removeFromLocation(activeGroups, activeLoc);
    passiveGroups = removeFromLocation(passiveGroups, passiveLoc);

    return {
      ...current,
      Active: activeGroups,
      Passive: passiveGroups
    };
  }

  const existingLoc = activeLoc ?? passiveLoc;
  let existingItem: ProjectContainerListItemModel | undefined;

  if (existingLoc) {
    const { groupIndex, containerIndex } = existingLoc;
    const sourceGroups = activeLoc ? activeGroups : passiveGroups;
    existingItem = sourceGroups[groupIndex].Containers[containerIndex];
  }

  // Remove from old status bucket if status changed
  if (activeLoc && !targetIsActive) {
    activeGroups = removeFromLocation(activeGroups, activeLoc);
  }
  if (passiveLoc && targetIsActive) {
    passiveGroups = removeFromLocation(passiveGroups, passiveLoc);
  }

  const targetGroups = targetIsActive ? activeGroups : passiveGroups;

  const baseItem: ProjectContainerListItemModel = existingItem ?? {
    Id: event.containerId,
    Name: event.name,
    Status: statusEnum,
    CreatedOn: new Date().toISOString(),
    UpdatedOn: null,
    UnreadCount: 0
  };

  const updatedItem: ProjectContainerListItemModel = {
    ...baseItem,
    Name: event.name,
    Status: statusEnum,
    UpdatedOn: new Date().toISOString()
  };

  const { groups: withGroup, groupIndex } = ensureAssignedGroup(targetGroups, event.companyId);
  const targetGroup = withGroup[groupIndex];

  const withoutDuplicate = targetGroup.Containers.filter((c) => c.Id !== event.containerId);
  const updatedGroup: ProjectContainerCompanyGroupModel = {
    ...targetGroup,
    Containers: [...withoutDuplicate, updatedItem]
  };

  const nextGroups = [...withGroup];
  nextGroups[groupIndex] = updatedGroup;

  if (targetIsActive) {
    activeGroups = nextGroups;
  } else {
    passiveGroups = nextGroups;
  }

  return {
    ...current,
    Active: activeGroups,
    Passive: passiveGroups
  };
};

export const applyProjectContainerListEventToTablePage = (
  current: PagedListReturn<ProjectContainerTableModel> | undefined,
  event: ProjectContainerListEvent
): PagedListReturn<ProjectContainerTableModel> | undefined => {
  if (!current) return current;

  const statusEnum = mapStatusStringToEnum(event.status);

  const rows = current.PagedRows ?? [];
  const index = rows.findIndex((r) => r.Id === event.containerId);

  if (event.type === 'Updated') {
    if (index === -1) return current;
    const updatedRows = rows.map((row) =>
      row.Id === event.containerId
        ? {
            ...row,
            Name: event.name,
            Status: statusEnum ?? row.Status
          }
        : row
    );
    return { ...current, PagedRows: updatedRows };
  }

  if (event.type === 'Created') {
    if (index !== -1) {
      // Treat as update if it already exists
      const updatedRows = rows.map((row) =>
        row.Id === event.containerId
          ? {
              ...row,
              Name: event.name,
              Status: statusEnum ?? row.Status
            }
          : row
      );
      return { ...current, PagedRows: updatedRows };
    }

    const newRow: ProjectContainerTableModel = {
      Id: event.containerId,
      Name: event.name,
      CompanyId: event.companyId,
      CompanyName: rows[0]?.CompanyName ?? '',
      Status: statusEnum ?? rows[0]?.Status ?? ProjectContainerStatus.Active,
      AssignedToUserName: null,
      AdditionalAssignedToUserNames: []
    };

    return {
      ...current,
      TotalRows: (current.TotalRows ?? rows.length) + 1,
      PagedRows: [newRow, ...rows]
    };
  }

  return current;
};
